// ruin.js — Phase 3 Option 2: build a procedural ruined wall corner from
// three user taps on the floor.
//
// State machine:
//   'idle'        — waiting for the corner tap (P0)
//   'one-marked'  — P0 placed; waiting for the wall-A end (P1)
//   'two-marked'  — P0+P1 placed; waiting for the wall-B end (P2)
//   'built'       — ruin geometry is in the scene; further taps no-op
//                   (use clearRuin() to redo)
//
// Geometry: each wall is a vertical strip from its floor segment up to
// roughly WALL_HEIGHT_M with a per-vertex jagged top. Stage 2 layers in
// a procedural canvas-based stone texture and scatters rubble cubes
// along each wall's base.

import * as THREE from 'three'

// Average wall height (meters) and how much each brick can dip below it
// (the "ruined" jaggedness — some bricks are full height, some are half,
// some are knocked down to nubs).
const WALL_HEIGHT_M = 1.0
const TOP_VARIANCE_M = 0.55

// Wall thickness in meters — each brick is this deep front-to-back.
// 35cm reads as an old, load-bearing stone wall (Roman / medieval).
const WALL_THICKNESS_M = 0.35

// How wide each brick is along the wall's length. The actual count is
// derived from the wall's length so brick width is consistent across
// walls of different lengths. ~30cm = chunky old-style blocks.
const BRICK_WIDTH_M = 0.3

// Rubble scatter along each wall base.
const RUBBLE_PER_WALL = 6
const RUBBLE_MIN_SIZE_M = 0.06
const RUBBLE_MAX_SIZE_M = 0.18
const RUBBLE_PERPENDICULAR_SPREAD_M = 0.15

// Marker visuals — distinct color per tap so the user can see which
// is which at a glance. Tap 1 = red corner, tap 2 = yellow A end,
// tap 3 = cyan B end.
const MARKER_RADIUS = 0.05
const MARKER_COLORS = [0xff3344, 0xffd54f, 0x44e0ff]
const BASE_LINE_COLOR = 0x00d8ff

let _scene = null
let _ruinGroup = null
let _state = 'idle'
let _points = []
let _markers = []
let _baseLines = []
let _wallMeshes = []
let _stoneMaterial = null

export function initRuin(scene) {
  _scene = scene
  _ruinGroup = new THREE.Group()
  _ruinGroup.name = 'ruin'
  scene.add(_ruinGroup)
  // Build the shared stone material once. Rebuilds per session in case
  // the GL context changed.
  _stoneMaterial = buildStoneMaterial()
  resetState()
}

export function getState() {
  return _state
}

export function getHint() {
  switch (_state) {
    case 'idle':
      return 'Tap the floor at the WALL CORNER (where 2 walls meet)'
    case 'one-marked':
      return 'Tap where one wall ENDS (along the floor)'
    case 'two-marked':
      return 'Now tap the OTHER wall — point at the second wall'
    case 'built':
      return '🏛️ Ruined — 🗑 to redo, 👁 to toggle'
    default:
      return ''
  }
}

// User tapped at this floor position. Returns the new state.
export function handleTap(point) {
  if (_state === 'built') return _state

  const p = point.clone()
  const tapIndex = _points.length // 0, 1, or 2 — also the marker color index
  _points.push(p)
  _markers.push(addMarker(p, MARKER_COLORS[tapIndex] ?? MARKER_COLORS[0]))

  if (_points.length === 1) {
    _state = 'one-marked'
  } else if (_points.length === 2) {
    _baseLines.push(addBaseLine(_points[0], _points[1]))
    _state = 'two-marked'
  } else if (_points.length === 3) {
    _baseLines.push(addBaseLine(_points[0], _points[2]))
    buildWall(_points[0], _points[1])
    buildWall(_points[0], _points[2])
    addRubble(_points[0], _points[1])
    addRubble(_points[0], _points[2])
    _state = 'built'
    // Once built, the tap markers + baselines are demo clutter; hide.
    setMarkersVisible(false)
  }

  console.info(`[ruin] ${_state} (${_points.length} taps)`)
  return _state
}

// Show/hide the construction-only visuals (tap markers + cyan
// baselines). Walls + rubble stay visible regardless. Called by
// handleTap on transition to 'built' and by clearRuin on reset.
export function setMarkersVisible(visible) {
  for (const m of _markers) m.visible = visible
  for (const l of _baseLines) l.visible = visible
}

// User asked to clear / redo. Wipes markers, lines, walls, resets state.
export function clearRuin() {
  for (const m of _markers) {
    _ruinGroup.remove(m)
    m.geometry.dispose()
    m.material.dispose()
  }
  for (const l of _baseLines) {
    _ruinGroup.remove(l)
    l.geometry.dispose()
    l.material.dispose()
  }
  for (const w of _wallMeshes) {
    _ruinGroup.remove(w)
    w.geometry.dispose()
    // Don't dispose _stoneMaterial — it's shared.
  }
  resetState()
  console.info('[ruin] cleared')
}

// Toggle the built ruin's visibility (the 👁 button uses this).
export function toggleVisible() {
  if (_state !== 'built') return
  _ruinGroup.visible = !_ruinGroup.visible
}

export function isBuilt() {
  return _state === 'built'
}

// ---------------------------------------------------------------------------

function resetState() {
  _state = 'idle'
  _points = []
  _markers = []
  _baseLines = []
  _wallMeshes = []
  if (_ruinGroup) _ruinGroup.visible = true
}

function addMarker(point, color) {
  const geometry = new THREE.SphereGeometry(MARKER_RADIUS, 16, 12)
  const material = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.95,
    depthTest: false
  })
  const mesh = new THREE.Mesh(geometry, material)
  mesh.position.copy(point)
  mesh.renderOrder = 600
  _ruinGroup.add(mesh)
  return mesh
}

function addBaseLine(a, b) {
  const geometry = new THREE.BufferGeometry().setFromPoints([a.clone(), b.clone()])
  const material = new THREE.LineBasicMaterial({
    color: BASE_LINE_COLOR,
    depthTest: false,
    transparent: true,
    opacity: 0.85
  })
  const line = new THREE.Line(geometry, material)
  line.renderOrder = 599
  _ruinGroup.add(line)
  return line
}

// Build a single ruined wall from `corner` to `endPoint` along the floor.
// The wall is constructed from N individual brick-shaped boxes side by
// side, each with its own random height — gives 3D depth to the jagged
// top edge and reads as a stone wall, not a 2D silhouette.
function buildWall(corner, endPoint) {
  const along = new THREE.Vector3().subVectors(endPoint, corner)
  const baseLen = along.length()
  if (baseLen < 1e-3) return // degenerate (corner == endPoint)
  along.normalize()

  // Perpendicular to the wall, in the floor plane. Used as the brick's
  // depth axis. (along × up = perpendicular gives a right-handed basis.)
  const up = new THREE.Vector3(0, 1, 0)
  const perpendicular = new THREE.Vector3().crossVectors(along, up).normalize()

  // How many bricks along this wall — at least 4 even for short walls,
  // more for longer ones, so the brick density looks consistent.
  const brickCount = Math.max(4, Math.round(baseLen / BRICK_WIDTH_M))
  const segWidth = baseLen / brickCount

  // Rotation that aligns box's local +X with `along`, +Z with
  // `perpendicular`, +Y with up.
  const basisMatrix = new THREE.Matrix4().makeBasis(along, up, perpendicular)
  const wallQuat = new THREE.Quaternion().setFromRotationMatrix(basisMatrix)

  for (let i = 0; i < brickCount; i++) {
    // Center of this brick along the base.
    const t = (i + 0.5) / brickCount
    const center = new THREE.Vector3().lerpVectors(corner, endPoint, t)

    // Per-brick randomness: height + small position jitter for character.
    const dip = Math.random() * TOP_VARIANCE_M
    const height = Math.max(0.08, WALL_HEIGHT_M - dip)

    // Slight Y offset so neighboring bricks aren't perfectly aligned at
    // the bottom — bricks "settle" a tiny bit.
    const yJitter = (Math.random() - 0.5) * 0.01

    // Position the brick's center: along the base + half-height up.
    center.y += height / 2 + yJitter

    // Slight depth jitter so the front face isn't a perfect plane.
    const depthJitter = (Math.random() - 0.5) * 0.015
    center.addScaledVector(perpendicular, depthJitter)

    const geometry = new THREE.BoxGeometry(segWidth, height, WALL_THICKNESS_M)
    const mesh = new THREE.Mesh(geometry, _stoneMaterial)
    mesh.position.copy(center)
    mesh.quaternion.copy(wallQuat)

    // Tiny rotational jitter for "settled stone" feel — keep small so
    // the wall still reads as a wall.
    mesh.rotateY((Math.random() - 0.5) * 0.06)
    mesh.rotateX((Math.random() - 0.5) * 0.04)
    mesh.rotateZ((Math.random() - 0.5) * 0.04)

    _ruinGroup.add(mesh)
    _wallMeshes.push(mesh)
  }
}

// Scatter a few small stone cubes along the wall's base, with a tiny
// perpendicular spread so they don't all sit exactly on the line.
function addRubble(corner, endPoint) {
  const along = new THREE.Vector3().subVectors(endPoint, corner).normalize()
  // Perpendicular in the floor plane (rotate 90° around Y).
  const perpendicular = new THREE.Vector3(-along.z, 0, along.x)

  for (let i = 0; i < RUBBLE_PER_WALL; i++) {
    const t = (i + 0.5) / RUBBLE_PER_WALL // distribute roughly evenly
    const jitter = (Math.random() - 0.5) * 0.15
    const tt = Math.max(0, Math.min(1, t + jitter))
    const pos = new THREE.Vector3().lerpVectors(corner, endPoint, tt)

    const lateral = (Math.random() - 0.5) * 2 * RUBBLE_PERPENDICULAR_SPREAD_M
    pos.addScaledVector(perpendicular, lateral)

    const size = RUBBLE_MIN_SIZE_M + Math.random() * (RUBBLE_MAX_SIZE_M - RUBBLE_MIN_SIZE_M)
    const geometry = new THREE.BoxGeometry(size, size, size)
    const mesh = new THREE.Mesh(geometry, _stoneMaterial)
    mesh.position.copy(pos)
    mesh.position.y += size / 2 // sit on the floor
    mesh.rotation.set(
      (Math.random() - 0.5) * 0.4,
      Math.random() * Math.PI * 2,
      (Math.random() - 0.5) * 0.4
    )
    _ruinGroup.add(mesh)
    _wallMeshes.push(mesh)
  }
}

// Procedural canvas-based stone material. Avoids a runtime fetch for an
// image asset and tiles seamlessly enough for a PoC. Stage 3 of polish
// would swap this for a real PBR texture set.
function buildStoneMaterial() {
  const size = 256
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')

  // Base sandstone tone.
  ctx.fillStyle = '#a68b6e'
  ctx.fillRect(0, 0, size, size)

  // Darker weathering blobs.
  for (let i = 0; i < 220; i++) {
    const x = Math.random() * size
    const y = Math.random() * size
    const r = 6 + Math.random() * 26
    const opacity = 0.06 + Math.random() * 0.22
    ctx.fillStyle = `rgba(70, 50, 30, ${opacity})`
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }

  // Lighter highlights.
  for (let i = 0; i < 80; i++) {
    const x = Math.random() * size
    const y = Math.random() * size
    const r = 4 + Math.random() * 14
    const opacity = 0.05 + Math.random() * 0.15
    ctx.fillStyle = `rgba(220, 200, 170, ${opacity})`
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }

  // Per-pixel noise grain so flat surfaces don't look perfectly flat.
  const imageData = ctx.getImageData(0, 0, size, size)
  for (let i = 0; i < imageData.data.length; i += 4) {
    const noise = (Math.random() - 0.5) * 24
    imageData.data[i] = clamp255(imageData.data[i] + noise)
    imageData.data[i + 1] = clamp255(imageData.data[i + 1] + noise)
    imageData.data[i + 2] = clamp255(imageData.data[i + 2] + noise)
  }
  ctx.putImageData(imageData, 0, 0)

  const texture = new THREE.CanvasTexture(canvas)
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 4

  return new THREE.MeshBasicMaterial({
    map: texture,
    side: THREE.DoubleSide
  })
}

function clamp255(v) {
  return Math.max(0, Math.min(255, v))
}
